import * as React from 'react';
import { Box, Container, Typography, Button, Grid, Link, styled, useTheme } from '@mui/material';
import ClockIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TargetIcon from '@mui/icons-material/TrackChanges';
import { ElectricBolt } from '@mui/icons-material';

const PatternBackgroundBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    inset: 0,
    backgroundImage: `url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTItNnYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=")`,
    opacity: 0.4,
    pointerEvents: 'none',
}));

const ComparisonBar = ({ traditionalTime, ncProTime, traditionalColor, ncProColor, traditionalWidth, ncProWidth }) => (
    <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>{traditionalTime}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: traditionalColor }}>Traditional: {traditionalTime}</Typography>
                <Typography variant="body2" sx={{ color: ncProColor, fontWeight: 700 }}>NC Pro: {ncProTime}</Typography>
            </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px', height: 12 }}>
            <Box sx={{ flex: 1, backgroundColor: traditionalColor, opacity: 0.3, borderRadius: 999 }} />
            <Box sx={{ width: ncProWidth, backgroundColor: ncProColor, borderRadius: 999 }} />
        </Box>
    </Box>
);

export default function SpeedDemo({ siteSettings = {} }) {
    const theme = useTheme();
    const s = siteSettings;

    return (

        <Box
            component="section"
            sx={{
                py: { xs: 8, md: 12 },
                background: 'linear-gradient(to bottom right, #0047AB, #1E5BB8, #0066CC)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <PatternBackgroundBox />
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 58,
                    background: 'linear-gradient(to bottom, white, transparent)',
                    zIndex: 5,
                }}
            />

            <Container maxWidth="xl" sx={{ px: 3, position: 'relative', zIndex: 10 }}>


                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: { xs: 8, md: 10 } }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: '#FBBF24',
                            px: 3,
                            py: 1,
                            borderRadius: 999,
                            mb: 2,
                        }}
                    >
                        <ElectricBolt sx={{ width: 20, height: 20, color: '#111827' }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            Proven Speed & Performance
                        </Typography>
                    </Box>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            fontWeight: 700,
                            mb: 3,
                            lineHeight: 1.1,
                        }}
                    >
                        We Don't Just Talk About Speed.
                        <br />
                        <Box component="span" sx={{ color: '#FDE047' }}>We Prove It.</Box>
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.25rem',
                            color: '#DBEAFE',
                        }}
                    >
                        Real benchmarks from real 8D workflows. See how NC Pro transforms hours into minutes.
                    </Typography>
                </Box>

                <Grid container spacing={4} sx={{ mb: { xs: 6, md: 8 } }}>
                    <Grid item xs={12} lg={4}>
                        <Box
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: '16px',
                                p: 4,
                                border: '2px solid rgba(255, 255, 255, 0.2)',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <ClockIcon sx={{ width: 48, height: 48, color: '#FDE047' }} />
                                <Typography sx={{ fontSize: '3rem', fontWeight: 700, color: '#FDE047' }}>{s.root_cause_analysis || '5 min'}</Typography>
                            </Box>
                            <Typography variant="h3" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 1.5 }}>Root Cause Analysis</Typography>
                            <Typography sx={{ color: '#DBEAFE', mb: 2 }}>
                                Traditional methods take 7-14 days. NC Pro's AI completes comprehensive root cause analysis in under 5 minutes.
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#A7F3D0', fontWeight: 600 }}>
                                <TrendingUpIcon sx={{ width: 20, height: 20 }} />
                                <Typography variant="body2" sx={{ color: '#A7F3D0', fontWeight: 600 }}>95% faster than manual</Typography>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                        <Box
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: '16px',
                                p: 4,
                                border: '2px solid rgba(255, 255, 255, 0.2)',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <TargetIcon sx={{ width: 48, height: 48, color: '#67E8F9' }} />
                                <Typography sx={{ fontSize: '3rem', fontWeight: 700, color: '#67E8F9' }}>{s.report_generation || '30sec'}</Typography>
                            </Box>
                            <Typography variant="h3" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 1.5 }}>Report Generation</Typography>
                            <Typography sx={{ color: '#DBEAFE', mb: 2 }}>
                                Manual report creation takes hours or days. NC Pro generates audit-ready 8D reports in 30 seconds with one click.
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#A7F3D0', fontWeight: 600 }}>
                                <TrendingUpIcon sx={{ width: 20, height: 20 }} />
                                <Typography variant="body2" sx={{ color: '#A7F3D0', fontWeight: 600 }}>99% time reduction</Typography>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                        <Box
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: '16px',
                                p: 4,
                                border: '2px solid rgba(255, 255, 255, 0.2)',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <ElectricBolt sx={{ width: 48, height: 48, color: '#A7F3D0' }} />
                                <Typography sx={{ fontSize: '3rem', fontWeight: 700, color: '#A7F3D0' }}>Instant</Typography>
                            </Box>
                            <Typography variant="h3" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 1.5 }}>Complete Setup</Typography>
                            <Typography sx={{ color: '#DBEAFE', mb: 2 }}>
                                Traditional systems require weeks of setup. NC Pro is fully operational in instant with zero configuration hassle.
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#A7F3D0', fontWeight: 600 }}>
                                <TrendingUpIcon sx={{ width: 20, height: 20 }} />
                                <Typography variant="body2" sx={{ color: '#A7F3D0', fontWeight: 600 }}>Immediate ROI</Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        background: 'linear-gradient(to right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px',
                        p: { xs: 4, md: 5 },
                        border: '2px solid rgba(255, 255, 255, 0.2)',
                    }}
                >
                    <Typography
                        variant="h3"
                        sx={{ fontSize: { xs: '1.5rem', md: '1.875rem' }, fontWeight: 700, mb: 4, textAlign: 'center' }}
                    >
                        Performance Benchmarks: NC Pro vs Traditional Methods
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                        <ComparisonBar
                            traditionalTime="Problem Definition (D2)"
                            ncProTime="5 minutes"
                            traditionalColor="#FCA5A5"
                            ncProColor="#86EFAC"
                            traditionalWidth="90%"
                            ncProWidth="50px"
                        />

                        <ComparisonBar
                            traditionalTime="Root Cause Ananlysis (D4)"
                            ncProTime="5 minutes"
                            traditionalColor="#FCA5A5"
                            ncProColor="#86EFAC"
                            traditionalWidth="90%"
                            ncProWidth="25px"
                        />


                        <ComparisonBar
                            traditionalTime="Corrective Action Planning (D5)"
                            ncProTime="15 minutes"
                            traditionalColor="#FCA5A5"
                            ncProColor="#86EFAC"
                            traditionalWidth="90%"
                            ncProWidth="75px"
                        />

                        <ComparisonBar
                            traditionalTime="Report Generation (D8)"
                            ncProTime="30 seconds"
                            traditionalColor="#FCA5A5"
                            ncProColor="#86EFAC"
                            traditionalWidth="90%"
                            ncProWidth="12px"
                        />
                    </Box>

                    <Grid container spacing={3} sx={{ mt: 5 }}>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                    backdropFilter: 'blur(4px)',
                                    borderRadius: '12px',
                                    p: 3,
                                    border: '1px solid rgba(248, 113, 113, 0.3)',
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#FCA5A5', fontWeight: 600, mb: 1 }}>Traditional Total Time</Typography>
                                <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: '#FCA5A5', mb: 1 }}>3-4 weeks</Typography>
                                <Typography variant="body2" sx={{ color: '#FECACA' }}>Per quality issue resolution</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                    backdropFilter: 'blur(4px)',
                                    borderRadius: '12px',
                                    p: 3,
                                    border: '1px solid rgba(52, 211, 153, 0.3)',
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#A7F3D0', fontWeight: 600, mb: 1 }}>NC Pro Total Time</Typography>
                                <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: '#A7F3D0', mb: 1 }}>1-2 hours</Typography>
                                <Typography variant="body2" sx={{ color: '#D1FAE5' }}>Per quality issue resolution</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Button
                        component={Link}
                        href="#demo"
                        variant="contained"
                        endIcon={<ElectricBolt sx={{ width: 24, height: 24, color: '#111827' }} />}
                        sx={{
                            backgroundColor: '#FBBF24',
                            color: '#111827',
                            px: 5,
                            py: 2.5,
                            borderRadius: '12px',
                            fontWeight: 700,
                            fontSize: '1.125rem',
                            textTransform: 'none',
                            boxShadow: theme.shadows[15],
                            transition: 'all 0.3s',
                            '&:hover': {
                                backgroundColor: '#FDE047',
                                transform: 'scale(1.05)',
                                boxShadow: theme.shadows[20],
                            },
                        }}
                    >
                        See the Speed for Yourself - Request Demo
                    </Button>
                </Box>
            </Container>
        </Box>
    );
}
